import { Sunrise, Sun, Sunset, Moon } from 'lucide-react';
import type { InGameTimeData, TimeOfDayPeriod } from '@danda/shared';
import { useGameStore } from '@/lib/stores/gameStore';
import { secondsToTimeData } from '@/lib/game-time';

const periodConfig: Record<TimeOfDayPeriod, { icon: typeof Sun; color: string; label: string }> = {
  morning: { icon: Sunrise, color: 'text-amber-500 dark:text-amber-300', label: 'Morning' },
  afternoon: { icon: Sun, color: 'text-yellow-500 dark:text-yellow-300', label: 'Afternoon' },
  evening: { icon: Sunset, color: 'text-orange-500 dark:text-orange-300', label: 'Evening' },
  night: { icon: Moon, color: 'text-blue-400 dark:text-blue-300', label: 'Night' },
};

interface GameTimeDisplayProps {
  /** Structured time data — takes priority over store and seconds */
  timeData?: InGameTimeData | null;
  /** Absolute game time in seconds — auto-converted to InGameTimeData */
  seconds?: number;
}

export function GameTimeDisplay({ timeData, seconds }: GameTimeDisplayProps) {
  const storeTime = useGameStore((s) => s.inGameTime);
  const time = timeData ?? (seconds != null ? secondsToTimeData(seconds) : storeTime);

  if (!time) return null;

  const period = (time.timeOfDay as TimeOfDayPeriod) || 'morning';
  const config = periodConfig[period] ?? periodConfig.morning;
  const Icon = config.icon;

  // Display day + 1 for players (internal day 0 = "Day 1")
  const displayDay = time.day + 1;
  const timeStr = `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;

  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Icon className={`h-3.5 w-3.5 ${config.color}`} />
      <span>
        Day {displayDay} {timeStr}
      </span>
      <span className={`${config.color}`}>{config.label}</span>
    </div>
  );
}
